<template>
  <BaseFieldset
    :class="$attrs.class"
    :flush="flush"
    :style="$attrs.style"
  >
    <BaseCheckbox
      v-if="type === 'checkbox'"
      :id="fieldIds.input"
      v-model="checkboxValue"
      v-bind="inputAttrs"
      :ariaDescribedby="error ? fieldIds.error : undefined"
      :ariaInvalid="!!error || undefined"
      :disabled="disabled"
      :label="label"
      :required="required"
    />

    <template v-else>
      <BaseLabel
        v-if="label"
        :html-for="fieldIds.input"
        :required="required"
      >
        {{ label }}
      </BaseLabel>

      <div
        v-if="hasAppendSlot"
        class="form-control-append"
      >
        <component
          :is="inputComponent"
          v-model="nonBooleanValue"
          v-bind="{ ...inputAttrs, ...componentProps } as SafeComponentProps"
          @blur="emit('blur')"
          @focus="emit('focus')"
        />
        <slot name="append" />
      </div>

      <component
        :is="inputComponent"
        v-else
        v-model="nonBooleanValue"
        v-bind="{ ...inputAttrs, ...componentProps } as SafeComponentProps"
        @blur="emit('blur')"
        @focus="emit('focus')"
      />
    </template>

    <BaseFormError
      :id="fieldIds.error"
      :message="error"
    />
  </BaseFieldset>
</template>

<script setup lang="ts">
import { computed, type Slots, useAttrs, useSlots } from 'vue';

import { useFormFieldIds } from '../../composables';
import {
  BaseCheckbox,
  BaseFieldset,
  BaseFormError,
  BaseInput,
  BaseLabel,
  BaseRadioGroup,
  BaseSelect,
  BaseTextarea,
  CurrencyInput,
  type RadioOption,
  type SelectEntry,
  type SelectOption,
  type SelectOptionGroup,
} from '../atoms';

defineOptions({
  inheritAttrs: false,
});

/**
 * Unified form control component
 *
 * Combines label, input, and error message into a single accessible form field.
 * Automatically handles different input types and accessibility attributes.
 *
 * @example Text input
 * ```vue
 * <FormControl
 *   v-model="form.email"
 *   type="email"
 *   label="Email Address"
 *   placeholder="user@example.com"
 *   :error="errors.email"
 *   required
 * />
 * ```
 *
 * @example Select dropdown
 * ```vue
 * <FormControl
 *   v-model="form.country"
 *   type="select"
 *   label="Country"
 *   :options="countries"
 * />
 * ```
 *
 * @example Currency input
 * ```vue
 * <FormControl
 *   v-model="form.amount"
 *   type="currency"
 *   label="Amount"
 *   currency="AUD"
 * />
 * ```
 *
 * @see {@link packages/ui-vue/src/components/molecules/FormControl.vue:1}
 */
type InputType =
  | 'text'
  | 'number'
  | 'date'
  | 'email'
  | 'time'
  | 'password'
  | 'tel'
  | 'url'
  | 'textarea'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'currency';

const {
  modelValue,
  label,
  type,
  placeholder,
  min,
  max,
  options,
  name,
  id,
  error,
  currency,
  disabled,
  readonly,
  required,
  flush,
} = defineProps<{
  /** Current value of the form control */
  modelValue: string | number | boolean;
  /** Label text displayed above the input */
  label?: string;
  /** Type of input to render */
  type: InputType;
  /** Placeholder text for text-based inputs */
  placeholder?: string;
  /** Minimum value for number/date inputs */
  min?: string | number;
  /** Maximum value for number/date inputs */
  max?: string | number;
  /** Options array for select/radio inputs (flat or grouped for select) */
  options?: Array<{
    id?: string;
    value?: string | number;
    title?: string;
    label?: string;
    group?: string;
    options?: SelectOption[];
  }>;
  /** Name attribute for form submission */
  name?: string;
  /** Custom ID (auto-generated if not provided) */
  id?: string;
  /** Error message to display */
  error?: string;
  /** Currency code for currency inputs (e.g., 'AUD', 'USD') */
  currency?: string;
  /** Disables the input */
  disabled?: boolean;
  /** Makes the input read-only */
  readonly?: boolean;
  /** Marks the field as required */
  required?: boolean;
  /** Removes bottom margin on the fieldset wrapper */
  flush?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean];
  focus: [];
  blur: [];
}>();

const slots: Slots = useSlots();
const hasAppendSlot = computed<boolean>(() => !!slots.append);

const attrs = useAttrs();

/**
 * Attrs to pass to the input component (excludes class/style which stay on wrapper)
 */
const inputAttrs = computed(() => {
  const { class: _c, style: _s, ...rest } = attrs;
  return rest;
});

const fieldIds = useFormFieldIds(id || 'field');

/**
 * Value accessor for non-checkbox components
 * Ensures type safety by excluding boolean values (checkbox is handled separately)
 * Converts boolean to string for edge cases
 */
const nonBooleanValue = computed({
  get(): string | number {
    if (typeof modelValue === 'boolean') {
      return modelValue ? 'true' : 'false';
    }
    return modelValue;
  },
  set(value: string | number) {
    emit('update:modelValue', value);
  },
});

/**
 * Value accessor for checkbox component
 * Ensures boolean type, with backward compatibility for string 'true'/'false' values
 */
const checkboxValue = computed({
  get() {
    if (typeof modelValue === 'string') {
      return modelValue === 'true';
    }
    return Boolean(modelValue);
  },
  set(value: boolean) {
    emit('update:modelValue', value);
  },
});

const inputComponent = computed(() => {
  switch (type) {
    case 'textarea':
      return BaseTextarea;
    case 'select':
      return BaseSelect;
    case 'radio':
      return BaseRadioGroup;
    case 'checkbox':
      return BaseCheckbox;
    case 'currency':
      return CurrencyInput;
    default:
      return BaseInput;
  }
});

const transformedOptions = computed((): SelectEntry[] | RadioOption[] => {
  if (!options) return [];

  return options.map(opt => {
    if (opt.group !== undefined) {
      return {
        group: opt.group,
        options: (opt.options ?? []).map(o => ({ value: o.value ?? '', label: o.label ?? '' })),
      } satisfies SelectOptionGroup;
    }
    return { value: opt.id ?? opt.value ?? '', label: opt.title ?? opt.label ?? '' } satisfies SelectOption;
  });
});

interface BaseComponentProps {
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
}

interface InputComponentProps extends BaseComponentProps {
  type?: 'text' | 'email' | 'number' | 'date' | 'time' | 'password' | 'tel' | 'url';
  min?: string | number;
  max?: string | number;
  readonly?: boolean;
}

interface SelectComponentProps extends BaseComponentProps {
  options: SelectEntry[];
  name?: string;
}

interface RadioComponentProps extends BaseComponentProps {
  options: RadioOption[];
  name?: string;
}

interface TextareaComponentProps extends BaseComponentProps {
  readonly?: boolean;
}

interface CurrencyComponentProps extends BaseComponentProps {
  currency?: string;
  min?: string | number;
  max?: string | number;
  readonly?: boolean;
}

type DynamicComponentProps =
  | InputComponentProps
  | SelectComponentProps
  | RadioComponentProps
  | TextareaComponentProps
  | CurrencyComponentProps;

type SafeComponentProps = Record<string, unknown> & DynamicComponentProps;

const componentProps = computed((): DynamicComponentProps => {
  const baseProps: BaseComponentProps = {
    placeholder,
    disabled,
    required,
    id: fieldIds.input,
    ariaDescribedby: error ? fieldIds.error : undefined,
    ariaInvalid: !!error || undefined,
  };

  switch (type) {
    case 'select':
      return {
        ...baseProps,
        options: transformedOptions.value as SelectEntry[],
        name,
      } satisfies SelectComponentProps;
    case 'radio':
      return {
        ...baseProps,
        options: transformedOptions.value as RadioOption[],
        name,
      } satisfies RadioComponentProps;
    case 'textarea':
      return {
        ...baseProps,
        readonly,
      } satisfies TextareaComponentProps;
    case 'currency':
      return {
        ...baseProps,
        currency,
        min,
        max,
        readonly,
      } satisfies CurrencyComponentProps;
    default:
      return {
        ...baseProps,
        type: type as InputComponentProps['type'],
        min,
        max,
        readonly,
      } satisfies InputComponentProps;
  }
});
</script>

<style>
.form-control-append {
  display: flex;
  align-items: stretch;
  gap: var(--gap-xs);

  > :first-child {
    flex: 1;
  }
}
</style>
